// Members (WI12): the list of households with their profile, and one household's page: who they are,
// their people and whom to call, consents and the links that ask for them, Medulance's registration,
// health details (only during an emergency, decision 12), tasks, payments and documents.
import { useEffect, useState } from "react";

import { api, openDocument, send, upload } from "./api";
import { CONSENT_KINDS, PACKS, ROLES, STATUSES, day, money, phone, toMinor, when } from "./format";
import { usePoll } from "./Pages";
import type { Household as HouseholdData, MemberRow, Person, Plan, Profile } from "./types";

// A change, then a reload; the problem, if any, shown where it happened.
export function useAct(reload: () => void) {
  const [problem, setProblem] = useState("");
  const act = async <T,>(fn: () => Promise<T>): Promise<T | null> => {
    setProblem("");
    try {
      const out = await fn();
      reload();
      return out;
    } catch (e) {
      setProblem(String((e as Error).message));
      return null;
    }
  };
  return { act, problem, setProblem };
}

export function Members({ onOpen }: { onOpen: (id: string) => void }) {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const query = new URLSearchParams({ ...(q ? { q } : {}), ...(status ? { status } : {}) }).toString();
  const { data, error, reload } = usePoll(() => api<MemberRow[]>(`/api/members${query ? `?${query}` : ""}`), 30000);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ name: "", payer_name: "", payer_email: "", payer_phone: "", status: "member", pack: "" });
  const { act, problem } = useAct(reload);

  const add = async () => {
    const made = await act(() =>
      send<{ id: string }>("/api/members", { ...draft, name: draft.name || draft.payer_name, pack: draft.pack || null }),
    );
    if (made?.id) {
      onOpen(made.id);
    }
  };

  return (
    <div className="stack wide">
      <div className="actions">
        <input className="grow" placeholder="Search by name, email or NID" value={q} onChange={(e) => (setQ(e.target.value), reload())} />
        <select value={status} onChange={(e) => (setStatus(e.target.value), reload())}>
          <option value="">Every status</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <button onClick={() => setAdding(!adding)}>{adding ? "Close" : "Add a member"}</button>
      </div>
      {adding && (
        <div className="panel">
          <div className="grid2">
            <label>
              Household name
              <input value={draft.name} placeholder="the payer's name if empty" onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
            </label>
            <label>
              Payer's name
              <input value={draft.payer_name} onChange={(e) => setDraft({ ...draft, payer_name: e.target.value })} />
            </label>
            <label>
              Payer's email
              <input value={draft.payer_email} onChange={(e) => setDraft({ ...draft, payer_email: e.target.value })} />
            </label>
            <label>
              Payer's WhatsApp number
              <input value={draft.payer_phone} placeholder="+1 555 0100" onChange={(e) => setDraft({ ...draft, payer_phone: e.target.value })} />
            </label>
            <label>
              Status
              <select value={draft.status} onChange={(e) => setDraft({ ...draft, status: e.target.value })}>
                {STATUSES.map((s) => (
                  <option key={s}>{s}</option>
                ))}
              </select>
            </label>
            <label>
              Pack
              <select value={draft.pack} onChange={(e) => setDraft({ ...draft, pack: e.target.value })}>
                <option value="">Not chosen</option>
                <option value="full">Full membership</option>
                <option value="lite">Lite</option>
              </select>
            </label>
          </div>
          <button disabled={!(draft.name || draft.payer_name).trim()} onClick={add}>
            Add
          </button>
        </div>
      )}
      {problem && <p className="note warn">{problem}</p>}
      {error && <p className="note warn">{error}</p>}
      {!data ? (
        <p className="muted">Loading…</p>
      ) : data.length === 0 ? (
        <p className="muted">No members{q || status ? " match" : " yet"}.</p>
      ) : (
        <table className="list">
          <thead>
            <tr>
              <th>Member</th>
              <th>Status</th>
              <th>Pack</th>
              <th>Assure</th>
              <th>City</th>
              <th>Open tasks</th>
            </tr>
          </thead>
          <tbody>
            {data.map((m) => (
              <tr key={m.household_id} onClick={() => onOpen(m.household_id)}>
                <td>
                  {m.payer_name || m.household_name} {m.nid && <span className="muted">{m.nid}</span>}
                  {m.pending_forms > 0 && <span className="badge warn">form to confirm</span>}
                </td>
                <td>{m.status}</td>
                <td>{m.pack ? PACKS[m.pack] : ""}</td>
                <td>{m.assure === "none" ? "" : m.assure}</td>
                <td>{m.city ?? ""}</td>
                <td>
                  {m.open_tasks} <span className="muted">of {m.tasks}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

const PROFILE_TEXT: [keyof Profile, string][] = [
  ["payer_name", "Payer's name"],
  ["payer_email", "Payer's email"],
  ["payer_phone", "Payer's WhatsApp number"],
  ["nid", "NID"],
  ["based_in", "Based in"],
  ["time_zone", "Time zone"],
  ["reach_by", "How to reach them"],
  ["reach_when", "When to reach them"],
  ["approver", "Who approves"],
  ["spend_limit", "Spend without asking, up to"],
  ["needs_approval", "Needs their approval"],
  ["never_without_asking", "Never without asking"],
  ["parents_language", "Parents' language"],
];
const ADDRESS = ["house", "street", "town", "city", "state", "pin"];

export function Household({ id, onTask }: { id: string; onTask: (taskId: string) => void }) {
  const { data, error, reload } = usePoll(() => api<HouseholdData>(`/api/members/${id}`), 30000);
  const { act, problem } = useAct(reload);
  const [editing, setEditing] = useState<Record<string, unknown> | null>(null);
  const [notice, setNotice] = useState<string[]>([]);
  const [health, setHealth] = useState<Record<string, Record<string, unknown>> | null>(null);

  if (error) {
    return <p className="note warn">{error}</p>;
  }
  if (!data) {
    return <p className="muted">Loading…</p>;
  }
  const p = data.profile;
  const parents = data.people.filter((x) => ["father", "mother", "grandparent"].includes(x.role) || x.contact_id);
  const consentedPeople = new Set(data.consents.filter((c) => c.kind === "parent_own" && !c.withdrawn_at).map((c) => c.person_id));

  const saveProfile = async () => {
    if (editing && (await act(() => send(`/api/members/${id}`, editing, "PUT"))) !== null) {
      setEditing(null);
    }
  };
  const showHealth = async () => {
    const out = await act(() => api<{ sections: Record<string, Record<string, unknown>> }>(`/api/members/${id}/health`));
    if (out) {
      setHealth(out.sections);
    }
  };

  return (
    <div className="household">
      <div className="case-head">
        <h2>
          {p.payer_name || p.household_name} <span className="muted">{p.nid}</span>
        </h2>
        <p>
          {p.status}
          {p.pack ? ` · ${PACKS[p.pack]}` : ""}
          {p.assure !== "none" ? ` · Assure ${p.assure}` : ""}
          {p.onboarded_at ? ` · onboarded ${day(p.onboarded_at)}` : ""}
          {p.activated_at ? ` · Assure since ${day(p.activated_at)}` : ""}
          {p.answers?.review ? " · " : ""}
          {p.answers?.review && <span className="badge warn">check: {String(p.answers.review.reason ?? "review")}</span>}
        </p>
      </div>
      {data.emergency_open && (
        <div className="banner">
          An emergency is open on this household: its health details can be shown, if a parent has given their own consent. Every look is
          recorded. <button onClick={showHealth}>Show health details</button>
        </div>
      )}
      {problem && <p className="note warn">{problem}</p>}
      {notice.length > 0 && (
        <div className="note panel">
          {notice.map((n, i) => (
            <div key={i}>{n}</div>
          ))}
          <button className="link" onClick={() => setNotice([])}>
            dismiss
          </button>
        </div>
      )}
      <Waiting data={data} household={id} act={act} onNotice={setNotice} />
      {health && (
        <div className="panel">
          <h3>Health details (shown for this emergency)</h3>
          {Object.entries(health).map(([section, values]) => (
            <dl key={section}>
              <h4>{section}</h4>
              {Object.entries(values).map(([k, v]) => (
                <div key={k}>
                  <dt>{k.replace(/_/g, " ")}</dt>
                  <dd>{String(v)}</dd>
                </div>
              ))}
            </dl>
          ))}
          <button className="secondary" onClick={() => setHealth(null)}>
            Hide
          </button>
        </div>
      )}

      <div className="columns">
        <section className="panel">
          <h3>
            Profile{" "}
            {!editing && (
              <button className="link" onClick={() => setEditing({})}>
                edit
              </button>
            )}
          </h3>
          {editing ? (
            <ProfileEditor profile={p} values={editing} onChange={setEditing} onSave={saveProfile} onCancel={() => setEditing(null)} />
          ) : (
            <dl>
              {PROFILE_TEXT.map(([k, label]) =>
                p[k] ? (
                  <div key={k}>
                    <dt>{label}</dt>
                    <dd>{k === "payer_phone" ? phone(String(p[k])) : String(p[k])}</dd>
                  </div>
                ) : null,
              )}
              <div>
                <dt>Address</dt>
                <dd>{ADDRESS.map((k) => p.address?.[k]).filter(Boolean).join(", ") || <span className="muted">none yet</span>}</dd>
              </div>
              <div>
                <dt>Family WhatsApp group</dt>
                <dd>{p.family_group ? "yes" : "no"}</dd>
              </div>
            </dl>
          )}
        </section>

        <section className="panel">
          <h3>People</h3>
          <People people={data.people} consented={consentedPeople} household={id} act={act} />
          <h3>Whom to call</h3>
          {data.contacts.length === 0 && <p className="muted">None given.</p>}
          {data.contacts.map((c) => (
            <p key={c.id}>
              {c.position}. {c.name || "Unnamed"} {c.relation && <span className="muted">({c.relation})</span>} {phone(c.phone)}{" "}
              {c.can_do && <span className="muted">· {c.can_do}</span>}
            </p>
          ))}
          <h3>Rung in an emergency</h3>
          <p className="muted">The parents at home the emergency path rings when the member writes from abroad.</p>
          {data.emergency_contacts.length === 0 && <p className="muted">Nobody yet: a parent's mobile adds them.</p>}
          {data.emergency_contacts.map((c) => (
            <p key={c.id}>
              {c.name || "Unnamed"} +{c.phone} <span className="muted">({c.relation})</span>
            </p>
          ))}
        </section>
      </div>

      <div className="columns">
        <section className="panel">
          <h3>Consents</h3>
          {data.consents.length === 0 && <p className="muted">None recorded.</p>}
          {data.consents.map((c) => (
            <p key={c.id} className={c.withdrawn_at ? "muted" : ""}>
              {CONSENT_KINDS[c.kind] ?? c.kind}
              {c.person_name ? ` · ${c.person_name}` : ""} · {c.method} · {day(c.given_at)}
              {c.note ? ` · ${c.note}` : ""}
              {c.withdrawn_at ? ` · withdrawn ${day(c.withdrawn_at)}` : ""}{" "}
              {!c.withdrawn_at && (
                <button
                  className="link"
                  onClick={() => window.confirm("Record that this consent was withdrawn?") && act(() => send(`/api/consents/${c.id}/withdraw`))}
                >
                  withdrawn
                </button>
              )}
            </p>
          ))}
          <CallConsent parents={parents} consented={consentedPeople} household={id} act={act} />
          <div className="actions">
            <button
              className="secondary"
              onClick={async () => {
                const out = await act(() =>
                  send<{ results: { name: string; sent: boolean; reason?: string; error?: string; url?: string }[] }>(
                    `/api/members/${id}/consent-requests`,
                  ),
                );
                if (out) {
                  setNotice(
                    out.results.length === 0
                      ? ["No parent on record to ask."]
                      : out.results.map((r) => `${r.name || "A parent"}: ${r.sent ? "sent" : r.reason || r.error}${r.url ? ` · try it: ${r.url}` : ""}`),
                  );
                }
              }}
            >
              Send the consent message
            </button>
            {(["assure", "onboarding"] as const).map((form) => (
              <button
                key={form}
                className="secondary"
                onClick={async () => {
                  const out = await act(() => send<{ url: string }>(`/api/members/${id}/form-link`, { form }));
                  if (out) {
                    setNotice([`The household's own ${form} form, for 60 days (shown once; it applies without waiting): ${out.url}`]);
                  }
                }}
              >
                {form === "assure" ? "Assure" : "Onboarding"} form link
              </button>
            ))}
          </div>
          <h3>Links</h3>
          {data.links.length === 0 && <p className="muted">None made.</p>}
          {data.links.map((l) => (
            <p key={l.id} className="muted">
              {l.purpose} ({l.language}) · made {day(l.created_at)} · {l.used_at ? `used ${day(l.used_at)}` : `until ${day(l.expires_at)}`}
              {l.sent?.template ? ` · ${l.sent.sent ? "sent" : "not sent"} by ${l.sent.template}` : ""}
            </p>
          ))}
        </section>

        <section className="panel">
          <h3>Medulance</h3>
          <p className="muted">Each parent's name, mobile, age, gender and address; never health details.</p>
          {parents.map((x) => (
            <p key={x.id}>
              {x.name || x.role}:{" "}
              {x.medulance?.registered ? (
                <span className="badge ok">registered</span>
              ) : (
                <span className="muted">{x.medulance?.reason || x.medulance?.error || "not yet"}</span>
              )}
            </p>
          ))}
          <button
            className="secondary"
            onClick={async () => {
              const out = await act(() =>
                send<{ results: { person: string; registered: boolean; reason?: string; error?: string }[] }>(`/api/members/${id}/medulance`),
              );
              if (out) {
                setNotice(
                  out.results.length === 0
                    ? ["No parent with a mobile on record."]
                    : out.results.map(
                        (r) =>
                          `${data.people.find((x) => x.id === r.person)?.name || "A parent"}: ${r.registered ? "registered" : r.reason || r.error}`,
                      ),
                );
              }
            }}
          >
            Register the parents
          </button>
          <h3>Health details</h3>
          {data.health.length === 0 ? (
            <p className="muted">None given.</p>
          ) : (
            data.health.map((h) => (
              <p key={h.section}>
                {h.section.startsWith("pending:") ? `From an ${h.section.split(":")[1]} form waiting for you` : `From the ${h.section} form`},{" "}
                {when(h.updated_at)}{" "}
                {h.policy_document_id && (
                  <button className="link" onClick={() => act(() => openDocument(h.policy_document_id!, "policy schedule"))}>
                    policy schedule
                  </button>
                )}
              </p>
            ))
          )}
          <p className="muted">Kept encrypted; shown only during an emergency, after a parent's own consent.</p>
        </section>
      </div>

      <section className="panel">
        <h3>Tasks</h3>
        <NewTask household={id} kind={p.status === "lead" ? "lead" : "member"} act={act} />
        {data.tasks.length > 0 && (
          <table className="list">
            <tbody>
              {data.tasks.map((t) => (
                <tr key={t.id} onClick={() => onTask(t.id)}>
                  <td>
                    {t.time_critical && <span className="badge warn">urgent</span>} {t.title}
                  </td>
                  <td>{t.category}</td>
                  <td>{t.status}</td>
                  <td>{t.target_closure ? day(t.target_closure) : ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <MemberPayments data={data} household={id} act={act} />

      <div className="columns">
        <section className="panel">
          <h3>Documents</h3>
          {data.documents.length === 0 && <p className="muted">None.</p>}
          {data.documents.map((d) => (
            <p key={d.id}>
              <button className="link" disabled={d.status !== "stored"} onClick={() => act(() => openDocument(d.id, d.filename))}>
                {d.filename || d.kind}
              </button>{" "}
              <span className="muted">
                {d.kind.replace(/_/g, " ")} · {day(d.created_at)}
              </span>
            </p>
          ))}
        </section>
        <section className="panel">
          <h3>Forms received</h3>
          {data.submissions.length === 0 && <p className="muted">None.</p>}
          {data.submissions.map((s) => (
            <details key={s.id}>
              <summary>
                {s.form} · {when(s.submitted_at)} {s.source === "import" && <span className="muted">(from the sheet)</span>}
                {s.status !== "applied" && <span className="muted"> · {s.status}</span>}
              </summary>
              <dl>
                {Object.entries(s.answers)
                  .filter(([k]) => !k.startsWith("_"))
                  .map(([k, v]) => (
                    <div key={k}>
                      <dt>{k.replace(/_/g, " ")}</dt>
                      <dd>{answerText(v)}</dd>
                    </div>
                  ))}
              </dl>
            </details>
          ))}
        </section>
      </div>
    </div>
  );
}

// An answer in words: several ticks joined; a matrix, or what the form asked that the platform has no
// field for, as question and answer.
const answerText = (value: unknown): string => {
  if (Array.isArray(value)) return value.map(answerText).join(", ");
  if (value && typeof value === "object") {
    return Object.entries(value as Record<string, unknown>)
      .map(([k, v]) => `${k}: ${answerText(v)}`)
      .join("; ");
  }
  return String(value ?? "");
};

// What ops check first in a waiting form: who the people are, their numbers, whom to call.
const WAITING_FIELDS: [string, string][] = [
  ["payer_name", "Payer"],
  ["payer_email", "Payer's email"],
  ["payer_phone", "Payer's WhatsApp number"],
  ["family_city", "Family's city"],
  ["parent1_name", "Parent 1"],
  ["parent1_phone", "Parent 1's mobile"],
  ["parent1_dob", "Parent 1's date of birth"],
  ["parent1_gender", "Parent 1's gender"],
  ["parent2_name", "Parent 2"],
  ["parent2_phone", "Parent 2's mobile"],
  ["parent2_dob", "Parent 2's date of birth"],
  ["parent2_gender", "Parent 2's gender"],
  ["call_first", "Call first"],
  ["call_next", "Call next"],
  ["needs_approval", "Needs their approval"],
  ["never_without_asking", "Never without asking"],
  ["father_name", "Father"],
  ["father_phone", "Father's mobile"],
  ["father_dob", "Father's date of birth"],
  ["mother_name", "Mother"],
  ["mother_phone", "Mother's mobile"],
  ["mother_dob", "Mother's date of birth"],
  ["grandparents_name", "Grandparents"],
  ["grandparents_phone", "Grandparents' mobile"],
  ["sibling_name", "Sibling"],
  ["sibling_phone", "Sibling's mobile"],
  ["staff_name", "Help at home"],
  ["staff_phone", "Help's mobile"],
  ["contact1_name", "Contact 1"],
  ["contact1_phone", "Contact 1's phone"],
  ["contact2_name", "Contact 2"],
  ["contact2_phone", "Contact 2's phone"],
  ["contact3_name", "Contact 3"],
  ["contact3_phone", "Contact 3's phone"],
  ["urgent_number", "Urgent number"],
];
const shown = (answers: Record<string, unknown>) => {
  const first = WAITING_FIELDS.filter(([k]) => answers[k]);
  const known = new Set(WAITING_FIELDS.map(([k]) => k));
  const rest = Object.keys(answers)
    .filter((k) => !known.has(k) && !k.startsWith("_") && answers[k] !== "" && answers[k] !== null)
    .map((k): [string, string] => [k, k.replace(/_/g, " ")]);
  return [...first, ...rest];
};

// Forms that wait for ops (decisions 1 and 5 after approval): an Assure form without the household's
// own link, or an onboarding form matched only by email. Nothing in them is applied or sent, and the
// emergency path keeps the numbers it has, until ops have checked with the family and confirm.
function Waiting({ data, household, act, onNotice }: { data: HouseholdData; household: string; act: Act; onNotice: (n: string[]) => void }) {
  const waiting = data.submissions.filter((s) => s.status === "pending");
  if (waiting.length === 0) {
    return null;
  }
  const confirm = async (id: string) => {
    if (
      !window.confirm(
        "Confirm this form? Its people, numbers, contacts and health details replace what's on record; after an Assure form the parents are registered and asked for consent where that's switched on.",
      )
    ) {
      return;
    }
    const out = await act(() =>
      send<{
        form: string;
        parents: string[];
        medulance: { registered: boolean; reason?: string; error?: string }[];
        consent: { sent: boolean; reason?: string; error?: string; url?: string }[];
      }>(`/api/members/${household}/forms/${id}/confirm`),
    );
    if (out) {
      onNotice([
        `Applied the ${out.form} form: ${out.parents.length} parent${out.parents.length === 1 ? "" : "s"}.`,
        ...out.medulance.map((r) => `Medulance: ${r.registered ? "registered" : r.reason || r.error}`),
        ...out.consent.map((r) => `Consent message: ${r.sent ? "sent" : r.reason || r.error}${r.url ? ` · try it: ${r.url}` : ""}`),
      ]);
    }
  };
  return (
    <div className="banner">
      <strong>{waiting.length === 1 ? "A form waits" : `${waiting.length} forms wait`} for you.</strong> Nothing in it changes this
      household, and the emergency path keeps the numbers it has, until you have checked it with the family.
      {waiting.map((s) => (
        <div key={s.id} className="panel">
          <p className="muted">
            {s.form === "assure" ? "Assure form without the household's own link" : "Onboarding form matched by the payer's email or number"}, sent{" "}
            {when(s.submitted_at)}
          </p>
          <dl>
            {shown(s.answers).map(([k, label]) => (
              <div key={k}>
                <dt>{label}</dt>
                <dd>{answerText(s.answers[k])}</dd>
              </div>
            ))}
          </dl>
          <div className="actions">
            <button onClick={() => confirm(s.id)}>Confirm and apply</button>
            <button
              className="secondary"
              onClick={() => window.confirm("Dismiss this form? Its health details are deleted.") && act(() => send(`/api/members/${household}/forms/${s.id}/dismiss`))}
            >
              Dismiss
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

function ProfileEditor({
  profile,
  values,
  onChange,
  onSave,
  onCancel,
}: {
  profile: Profile;
  values: Record<string, unknown>;
  onChange: (v: Record<string, unknown>) => void;
  onSave: () => void;
  onCancel: () => void;
}) {
  const value = (k: string) => String(k in values ? (values[k] ?? "") : ((profile as unknown as Record<string, unknown>)[k] ?? ""));
  const address = (values.address as Record<string, string>) ?? profile.address ?? {};
  return (
    <div>
      <div className="grid2">
        <label>
          Status
          <select value={value("status")} onChange={(e) => onChange({ ...values, status: e.target.value })}>
            {STATUSES.map((s) => (
              <option key={s}>{s}</option>
            ))}
          </select>
        </label>
        <label>
          Pack
          <select value={value("pack")} onChange={(e) => onChange({ ...values, pack: e.target.value || null })}>
            <option value="">Not chosen</option>
            <option value="full">Full membership</option>
            <option value="lite">Lite</option>
          </select>
        </label>
        <label>
          Niro Assure
          <select value={value("assure")} onChange={(e) => onChange({ ...values, assure: e.target.value })}>
            <option value="none">none</option>
            <option value="requested">requested</option>
            <option value="active">active</option>
          </select>
        </label>
        {PROFILE_TEXT.map(([k, label]) => (
          <label key={k}>
            {label}
            <input value={value(k)} onChange={(e) => onChange({ ...values, [k]: e.target.value })} />
          </label>
        ))}
        {ADDRESS.map((k) => (
          <label key={k}>
            Address: {k}
            <input value={address[k] ?? ""} onChange={(e) => onChange({ ...values, address: { ...address, [k]: e.target.value } })} />
          </label>
        ))}
      </div>
      <label className="check">
        <input
          type="checkbox"
          checked={Boolean("family_group" in values ? values.family_group : profile.family_group)}
          onChange={(e) => onChange({ ...values, family_group: e.target.checked })}
        />
        Family WhatsApp group set up
      </label>
      <div className="actions">
        <button onClick={onSave}>Save</button>
        <button className="secondary" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}

type Act = <T>(fn: () => Promise<T>) => Promise<T | null>;

function People({ people, consented, household, act }: { people: Person[]; consented: Set<string | null>; household: string; act: Act }) {
  const blank = { role: "father", name: "", phone: "", date_of_birth: "", gender: "", language: "", whatsapp_comfort: "", notes: "" };
  const [draft, setDraft] = useState<Record<string, string> | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const save = async () => {
    if (!draft) {
      return;
    }
    const body = Object.fromEntries(Object.entries(draft).filter(([, v]) => v !== ""));
    const path = editing ? `/api/people/${editing}` : `/api/members/${household}/people`;
    if ((await act(() => send(path, body, editing ? "PUT" : "POST"))) !== null) {
      setDraft(null);
      setEditing(null);
    }
  };
  return (
    <>
      {people.length === 0 && <p className="muted">Nobody yet.</p>}
      {people.map((x) => (
        <p key={x.id}>
          <strong>{x.role}</strong> {x.name} {phone(x.phone)}
          {x.date_of_birth ? ` · born ${day(x.date_of_birth)}` : ""}
          {x.gender ? ` · ${x.gender}` : ""}
          {x.language ? ` · ${x.language}` : ""}
          {consented.has(x.id) && <span className="badge ok">consented</span>}{" "}
          <button
            className="link"
            onClick={() => {
              setEditing(x.id);
              setDraft({
                role: x.role,
                name: x.name,
                phone: x.phone ? `+${x.phone}` : "",
                date_of_birth: x.date_of_birth ?? "",
                gender: x.gender ?? "",
                language: x.language,
                whatsapp_comfort: x.whatsapp_comfort,
                notes: x.notes,
              });
            }}
          >
            edit
          </button>
        </p>
      ))}
      {draft ? (
        <div className="grid2">
          {!editing && (
            <label>
              Role
              <select value={draft.role} onChange={(e) => setDraft({ ...draft, role: e.target.value })}>
                {ROLES.map((r) => (
                  <option key={r}>{r}</option>
                ))}
              </select>
            </label>
          )}
          <label>
            Name
            <input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
          </label>
          <label>
            Mobile
            <input value={draft.phone} placeholder="+91 …" onChange={(e) => setDraft({ ...draft, phone: e.target.value })} />
          </label>
          <label>
            Date of birth
            <input type="date" value={draft.date_of_birth} onChange={(e) => setDraft({ ...draft, date_of_birth: e.target.value })} />
          </label>
          <label>
            Gender
            <select value={draft.gender} onChange={(e) => setDraft({ ...draft, gender: e.target.value })}>
              <option value="" />
              <option value="female">female</option>
              <option value="male">male</option>
              <option value="other">other</option>
            </select>
          </label>
          <label>
            Language
            <input value={draft.language} placeholder="English, Hindi or Hinglish" onChange={(e) => setDraft({ ...draft, language: e.target.value })} />
          </label>
          <label>
            WhatsApp comfort
            <input value={draft.whatsapp_comfort} onChange={(e) => setDraft({ ...draft, whatsapp_comfort: e.target.value })} />
          </label>
          <div className="actions">
            <button onClick={save}>Save</button>
            <button className="secondary" onClick={() => (setDraft(null), setEditing(null))}>
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <button className="secondary" onClick={() => setDraft(blank)}>
          Add a person
        </button>
      )}
    </>
  );
}

function CallConsent({ parents, consented, household, act }: { parents: Person[]; consented: Set<string | null>; household: string; act: Act }) {
  const [person, setPerson] = useState("");
  const [note, setNote] = useState("");
  const waiting = parents.filter((x) => !consented.has(x.id));
  if (waiting.length === 0) {
    return null;
  }
  return (
    <div className="actions">
      <select value={person} onChange={(e) => setPerson(e.target.value)}>
        <option value="">A parent who agreed on a call…</option>
        {waiting.map((x) => (
          <option key={x.id} value={x.id}>
            {x.name || x.role}
          </option>
        ))}
      </select>
      <input className="grow" placeholder="Note: when, who called" value={note} onChange={(e) => setNote(e.target.value)} />
      <button
        disabled={!person}
        onClick={async () =>
          (await act(() => send(`/api/members/${household}/consents`, { person_id: person, note }))) !== null && (setPerson(""), setNote(""))
        }
      >
        Record their consent
      </button>
    </div>
  );
}

export function NewTask({ household, kind, act }: { household: string; kind: string; act: Act }) {
  const [title, setTitle] = useState("");
  return (
    <div className="actions">
      <input className="grow" placeholder="A new task" value={title} onChange={(e) => setTitle(e.target.value)} />
      <button
        disabled={!title.trim()}
        onClick={async () => (await act(() => send("/api/tasks", { household_id: household, kind, title }))) !== null && setTitle("")}
      >
        Add task
      </button>
    </div>
  );
}

function MemberPayments({ data, household, act }: { data: HouseholdData; household: string; act: Act }) {
  const [order, setOrder] = useState({ kind: "service", description: "", currency: "INR", amount: "", task_id: "", link: true });
  const [plan, setPlan] = useState("");
  const [plans, setPlans] = useState<Plan[] | null>(null);
  const [passThrough, setPassThrough] = useState(false); // off until Akshat says how they work (decision 8)
  const [made, setMade] = useState("");
  useEffect(() => {
    api<{ plans: Plan[]; pass_through: boolean }>("/api/billing")
      .then((b) => {
        setPlans(b.plans.filter((x) => x.active && x.period));
        setPassThrough(b.pass_through);
      })
      .catch(() => setPlans([]));
  }, []);
  const createOrder = async () => {
    const out = await act(() =>
      send<{ id: string; url: string | null }>(`/api/members/${household}/orders`, {
        kind: order.kind,
        description: order.description,
        currency: order.currency,
        amount_minor: toMinor(order.amount),
        task_id: order.task_id || null,
        link: order.link,
      }),
    );
    if (out) {
      setMade(out.url ? `Payment link: ${out.url}` : "Order noted, without a link.");
      setOrder({ ...order, description: "", amount: "" });
    }
  };
  return (
    <section className="panel">
      <h3>Payments</h3>
      {made && <p className="note">{made}</p>}
      {data.subscriptions.map((s) => (
        <p key={s.id}>
          Subscription: {s.plan_name} ({money(s.amount_minor, s.currency)} a {s.period}) · {s.status}
          {s.current_end ? ` · paid until ${day(s.current_end)}` : ""}{" "}
          {s.short_url && s.status === "created" && <span className="muted">authorise at {s.short_url}</span>}{" "}
          {!["cancelled", "ended"].includes(s.status) && (
            <button
              className="link"
              onClick={() => window.confirm("End this subscription? No further period is charged.") && act(() => send(`/api/subscriptions/${s.id}/cancel`))}
            >
              end it
            </button>
          )}
        </p>
      ))}
      <div className="actions">
        <select value={plan} onChange={(e) => setPlan(e.target.value)}>
          <option value="">A membership plan…</option>
          {(plans ?? []).map((x) => (
            <option key={x.id} value={x.id}>
              {x.name}: {money(x.amount_minor, x.currency)} a {x.period}
            </option>
          ))}
        </select>
        <button
          disabled={!plan}
          onClick={async () => {
            const out = await act(() => send<{ url: string | null }>(`/api/members/${household}/subscriptions`, { plan_id: plan }));
            if (out) {
              setMade(`Subscription made: the member authorises the card at ${out.url}`);
            }
          }}
        >
          Start the subscription
        </button>
      </div>
      {data.orders.length > 0 && (
        <table className="list">
          <thead>
            <tr>
              <th>For</th>
              <th>Amount</th>
              <th>Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {data.orders.map((o) => (
              <tr key={o.id}>
                <td>
                  {o.description} <span className="muted">({o.kind.replace("_", "-")})</span>
                  {o.task_title && <div className="muted">task: {o.task_title}</div>}
                </td>
                <td>{money(o.amount_minor, o.currency)}</td>
                <td>
                  {o.status}
                  {o.short_url && o.status === "sent" && <div className="muted">{o.short_url}</div>}
                </td>
                <td>
                  <span className="actions">
                    {o.receipt_document_id ? (
                      <button className="link" onClick={() => act(() => openDocument(o.receipt_document_id!, "receipt"))}>
                        receipt
                      </button>
                    ) : (
                      <label className="link-like">
                        add receipt
                        <input
                          type="file"
                          accept="application/pdf,image/*"
                          hidden
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              const form = new FormData();
                              form.append("file", file);
                              act(() => upload(`/api/orders/${o.id}/receipt`, form));
                            }
                          }}
                        />
                      </label>
                    )}
                    {!["paid", "cancelled"].includes(o.status) && !o.provider_link_id && (
                      <button className="link" onClick={() => act(() => send(`/api/orders/${o.id}/status`, { status: "paid" }))}>
                        paid
                      </button>
                    )}
                    {!["paid", "cancelled"].includes(o.status) && (
                      <button className="link" onClick={() => window.confirm("Cancel this order?") && act(() => send(`/api/orders/${o.id}/cancel`))}>
                        cancel
                      </button>
                    )}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div className="grid2">
        <label>
          What for
          <select value={order.kind} onChange={(e) => setOrder({ ...order, kind: e.target.value })}>
            {passThrough && <option value="pass_through">A pass-through cost</option>}
            <option value="service">A one-off service</option>
            <option value="membership">A membership period</option>
          </select>
        </label>
        <label>
          Description
          <input value={order.description} onChange={(e) => setOrder({ ...order, description: e.target.value })} />
        </label>
        <label>
          Amount
          <span className="actions tight">
            <select value={order.currency} onChange={(e) => setOrder({ ...order, currency: e.target.value })}>
              <option>INR</option>
              <option>USD</option>
            </select>
            <input inputMode="decimal" value={order.amount} onChange={(e) => setOrder({ ...order, amount: e.target.value })} />
          </span>
        </label>
        <label>
          Against a task
          <select value={order.task_id} onChange={(e) => setOrder({ ...order, task_id: e.target.value })}>
            <option value="">None</option>
            {data.tasks.map((t) => (
              <option key={t.id} value={t.id}>
                {t.title}
              </option>
            ))}
          </select>
        </label>
      </div>
      <label className="check">
        <input type="checkbox" checked={order.link} onChange={(e) => setOrder({ ...order, link: e.target.checked })} />
        Make a payment link (when payments are on)
      </label>
      <button disabled={!order.description.trim() || !(toMinor(order.amount) > 0)} onClick={createOrder}>
        Add the order
      </button>
      {data.payments.length > 0 && (
        <>
          <h3>Paid</h3>
          {data.payments.map((x) => (
            <p key={x.id}>
              {money(x.amount_minor, x.currency)} · {x.status} · {when(x.captured_at ?? x.created_at)}
              {x.invoice_number && ` · invoice ${x.invoice_number}`}
            </p>
          ))}
        </>
      )}
    </section>
  );
}
